import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  GridLegacy as Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  CloudUpload,
  ExpandMore,
  Download,
  Assessment,
  Memory,
  Speed,
  Storage,
  AttachMoney,
  GetApp,
  Code,
  Settings,
  NetworkCheck,
  Speed as SpeedIcon,
  Savings,
  Dns,
  AutoAwesome
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { useAnalysis } from './AnalysisContext';
import { PageHeader, KpiTile, GRADIENTS } from './components/ui';
import { chartColors, chartColorsSoft } from './theme';
import { useThemeMode } from './ThemeModeContext';
import { buildReport } from './pdfReport';

ChartJS.defaults.animation = { duration: 1100, easing: 'easeOutQuart' };
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

export default function SSATAnalyzer() {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const { palette } = useThemeMode();   // re-render (chart colours) when the palette changes
  const { results, setResults, activeUploadId, activeTitle, clearResults, userEmail, markNewAnalysis, shareAnalysis } = useAnalysis();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [includeEC2, setIncludeEC2] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [shareEmail, setShareEmail] = useState('');
  const [shareMsg, setShareMsg] = useState(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/zip': ['.zip']
    },
    multiple: true,
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles);
      setError(null);
    }
  });

  const toggleRowExpansion = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const removeFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (!customerName.trim()) { setError('Please enter a Customer / Workload Name'); return; }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      if (files.length === 1) {
        const formData = new FormData();
        formData.append('ssatFile', files[0]);
        formData.append('recommendationType', 'RDS');
        formData.append('includeEC2', includeEC2.toString());
        formData.append('customerName', customerName.trim());
        formData.append('userEmail', userEmail || '');

        const response = await fetch('/api/ssat/analyze', {
          method: 'POST',
          headers: {
            ...getAuthHeaders()
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const result = await response.json();
        setResults(result);
        markNewAnalysis();
      } else {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append('ssatFiles', file);
        });
        formData.append('recommendationType', 'RDS');
        formData.append('includeEC2', includeEC2.toString());
        formData.append('customerName', customerName.trim());
        formData.append('userEmail', userEmail || '');

        const response = await fetch('/api/ssat/batch-analyze', {
          method: 'POST',
          headers: {
            ...getAuthHeaders()
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Batch upload failed: ${response.statusText}`);
        }

        const result = await response.json();
        setResults(result);
        markNewAnalysis();
      }
    } catch (err) {
      setError(err.message || t('uploadFailedGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const exportResults = () => {
    if (!results) return;
    
    const dataStr = JSON.stringify(results, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${customerName.trim() || 'ssat'}-analysis-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    if (!results) return;
    const rows = [];
    const header = 'Server,CPU P95%,Memory P95%,IOPS P95,IOPS Max,Throughput P95 (MB/s),Cores,Memory GB,Instance Type,vCPUs,Instance Memory GB,Monthly Cost,Remark';
    rows.push(header);

    if (results.batchMode) {
      (results.results || []).filter(r => r.success).forEach(r => {
        const rec = r.recommendations?.[0] || {};
        rows.push([r.serverName, r.analysis?.cpu?.p95?.toFixed(1), r.analysis?.memory?.p95?.toFixed(1),
          Math.round(r.analysis?.iops?.p95 || 0), Math.round(r.analysis?.iops?.max || 0), (r.analysis?.throughput?.p95 || 0).toFixed(0), r.analysis?.currentServer?.cores || '', r.analysis?.currentServer?.memoryGB || '',
          rec.instanceType || '', rec.specs?.cpu || '', rec.specs?.memory || '', rec.monthlyPrice || '',
          `"${(rec.explanation || '').replace(/"/g, '""')}"`].join(','));
      });
    } else {
      const rec = results.recommendations?.[0] || {};
      rows.push([results.serverName, results.analysis?.cpu?.p95?.toFixed(1), results.analysis?.memory?.p95?.toFixed(1),
        Math.round(results.analysis?.iops?.p95 || 0), Math.round(results.analysis?.iops?.max || 0), (results.analysis?.throughput?.p95 || 0).toFixed(0), results.analysis?.currentServer?.cores || '', results.analysis?.currentServer?.memoryGB || '',
        rec.instanceType || '', rec.specs?.cpu || '', rec.specs?.memory || '', rec.monthlyPrice || '',
        `"${(rec.explanation || '').replace(/"/g, '""')}"`].join(','));
    }

    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${customerName.trim() || 'ssat'}-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!results) return;
    const { doc, fileName } = buildReport({ results, customerName });
    doc.save(fileName);
  };

  const downloadSSATCollector = () => {
    const link = document.createElement('a');
    link.href = '/SSATcollector.zip';
    link.download = 'SSATcollector.zip';
    link.click();
  };

  const getPerformanceChartData = (analysis) => {
    return {
      labels: ['Average', 'Maximum', '95th Percentile'],
      datasets: [
        {
          label: 'CPU %',
          data: [analysis.cpu.avg, analysis.cpu.max, analysis.cpu.p95],
          borderColor: chartColors(palette)[0],
          backgroundColor: chartColors(palette)[0] + '26',
        },
        {
          label: 'Memory %',
          data: [analysis.memory.avg, analysis.memory.max, analysis.memory.p95],
          borderColor: chartColors(palette)[1],
          backgroundColor: chartColors(palette)[1] + '26',
        }
      ],
    };
  };

  const getCostComparisonData = (recommendations) => {
    return {
      labels: recommendations.map(rec => rec.instanceType),
      datasets: [
        {
          label: t('monthlyCostDollar'),
          data: recommendations.map(rec => rec.monthlyPrice),
          backgroundColor: chartColorsSoft(palette),
        }
      ],
    };
  };

  return (
    <Box>
      {!results && (
        <Box className="anim-pop" sx={{
          position: 'relative', overflow: 'hidden', borderRadius: 4, mb: 3, p: { xs: 3, md: 5 }, color: '#fff',
          background: GRADIENTS.hero, backgroundSize: '220% 220%', animation: 'gradientShift 14s ease infinite',
          boxShadow: '0 18px 44px rgb(var(--c-primary-rgb) / 0.32)',
        }}>
          <Box sx={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', top: -120, right: -60, background: 'radial-gradient(circle, rgba(255,255,255,0.35), transparent 65%)', animation: 'blobMove 12s ease-in-out infinite' }} />
          <Box sx={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', bottom: -110, left: '38%', background: 'radial-gradient(circle, rgba(255,255,255,0.22), transparent 65%)', animation: 'blobMove 16s ease-in-out infinite reverse' }} />
          <Box sx={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', top: 30, right: '22%', border: '2px solid rgba(255,255,255,0.35)', animation: 'floatY 6s ease-in-out infinite' }} />
          <Box sx={{ position: 'relative', maxWidth: 760 }}>
            <Chip icon={<AutoAwesome sx={{ color: '#fff !important' }} />} label={t('analyzer.badge', 'Workload-based right-sizing')} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', backdropFilter: 'blur(6px)', mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.03em', fontSize: { xs: 28, md: 40 }, lineHeight: 1.1 }}>
              {t('analyzer.heading', 'Right-size your SQL Server workload')}
            </Typography>
            <Typography sx={{ mt: 1.5, opacity: 0.92, fontSize: { xs: 15, md: 17 }, maxWidth: 620 }}>
              {t('analyzer.subheading', 'Upload the SSAT collector output to get an Amazon RDS recommendation based on the real workload.')}
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {[[<SpeedIcon fontSize="small" />, 'Real CPU, memory & IOPS'], [<Dns fontSize="small" />, 'Edition-aware sizing'], [<Savings fontSize="small" />, 'Live AWS pricing']].map(([ic, label], i) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 0.9, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.22)', fontSize: 13.5, fontWeight: 600, animation: 'fadeUp .6s ease both', animationDelay: `${300 + i * 120}ms` }}>
                  {ic}{label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <PageHeader
        title={results ? (activeTitle || customerName || t('nav.newAnalysis', 'Analysis')) : ''}
        subtitle={results ? t('analyzer.resultSub', 'Recommendation based on the collected workload.') : ''}
        actions={results ? (
          <>
            <Button variant="outlined" size="small" onClick={exportResults} startIcon={<Download />}>JSON</Button>
            <Button variant="outlined" size="small" onClick={exportCSV} startIcon={<Download />}>CSV</Button>
            <Button variant="outlined" size="small" onClick={exportPDF} startIcon={<Download />}>PDF</Button>
            <Button variant="contained" size="small" onClick={() => { clearResults(); setFiles([]); }} startIcon={<CloudUpload />}>{t('analyzer.startNew', 'New analysis')}</Button>
          </>
        ) : null}
      />

      {results && activeUploadId && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 3, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ mr: 1 }}>{t('analyzer.share', 'Share this analysis')}</Typography>
          <TextField type="email" placeholder={t('history.email', 'Email address')} value={shareEmail} onChange={e => setShareEmail(e.target.value)} sx={{ width: 280 }} />
          <Button
            variant="outlined"
            disabled={!shareEmail.trim()}
            onClick={async () => {
              const r = await shareAnalysis(activeUploadId, shareEmail);
              setShareMsg(r);
              if (r.ok) setShareEmail('');
              setTimeout(() => setShareMsg(null), 3500);
            }}
          >
            {t('history.share', 'Share')}
          </Button>
          {shareMsg && <Typography variant="body2" color={shareMsg.ok ? 'success.main' : 'error.main'}>{shareMsg.message}</Typography>}
        </Paper>
      )}

      {!results && (
      <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Upload card */}
          <Grid item xs={12} lg={8}>
            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, height: '100%' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CloudUpload color="primary" />
                {t('analyzer.upload', 'Upload SSAT files')}
              </Typography>
  
              <Box
                {...getRootProps()}
                sx={{
                  borderRadius: 3,
                  py: 5, px: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? 'rgb(var(--c-primary-rgb) / 0.16)' : 'rgb(var(--c-primary-rgb) / 0.05)',
                  backgroundImage: 'linear-gradient(90deg,var(--c-primary) 50%,transparent 50%),linear-gradient(90deg,var(--c-primary) 50%,transparent 50%),linear-gradient(0deg,var(--c-primary) 50%,transparent 50%),linear-gradient(0deg,var(--c-primary) 50%,transparent 50%)',
                  backgroundRepeat: 'repeat-x, repeat-x, repeat-y, repeat-y',
                  backgroundSize: '12px 2px, 12px 2px, 2px 12px, 2px 12px',
                  backgroundPosition: '0 0, 0 100%, 0 0, 100% 0',
                  animation: 'marchAnts 1.1s linear infinite',
                  transform: isDragActive ? 'scale(1.02)' : 'none',
                  transition: 'transform .25s ease, background-color .25s ease',
                  '&:hover': { bgcolor: 'rgb(var(--c-primary-rgb) / 0.10)' },
                }}
              >
                <input {...getInputProps()} />
                <Box sx={{ width: 60, height: 60, borderRadius: '50%', background: GRADIENTS.brand, color: '#fff', display: 'grid', placeItems: 'center', mx: 'auto', mb: 2, animation: isDragActive ? 'bounceUp .7s ease infinite' : 'pulseRing 2.6s ease-out infinite' }}>
                  <CloudUpload />
                </Box>
                <Typography variant="h6" gutterBottom>{isDragActive ? t('dragActive') : t('dragDrop')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('clickToSelect')}</Typography>
              </Box>
  
              {files.length > 0 && (
                <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {files.map((file, index) => (
                    <Chip
                      key={index}
                      label={`${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB`}
                      onDelete={() => removeFile(index)}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              )}
  
              <TextField
                fullWidth
                required
                label={t('analyzer.customer', 'Customer / workload name')}
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                sx={{ mt: 3 }}
              />
  
              <Box sx={{ mt: 2.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleUpload}
                  disabled={files.length === 0 || loading}
                  startIcon={<Assessment />}
                >
                  {loading ? 'Analyzing...' : `Analyze ${files.length} file${files.length !== 1 ? 's' : ''}`}
                </Button>
                <FormControlLabel
                  control={<Checkbox size="small" checked={includeEC2} onChange={(e) => setIncludeEC2(e.target.checked)} />}
                  label={<Typography variant="body2">Include EC2 self-managed recommendations <Typography component="span" variant="caption" color="text.secondary">(slower)</Typography></Typography>}
                />
              </Box>
  
              {loading && (
                <Box sx={{ mt: 2.5 }}>
                  <LinearProgress />
                  <Typography className="analyzing-dots" variant="body2" color="primary" sx={{ mt: 1, fontWeight: 600 }}>{t('processingFiles')}</Typography>
                </Box>
              )}
              {error && <Alert severity="error" sx={{ mt: 2.5 }}>{error}</Alert>}
            </Paper>
          </Grid>
  
          {/* Collector + tips */}
          <Grid item xs={12} lg={4}>
            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Code color="primary" />
                {t('ssatCollection')}
              </Typography>
              <Typography variant="body2" color="text.secondary">{t('downloadDescription')}</Typography>
              <Button variant="contained" color="primary" onClick={downloadSSATCollector} startIcon={<GetApp />} fullWidth>
                {t('downloadButton')}
              </Button>
              <Divider />
              <Typography variant="subtitle2">{t('analyzer.tips', 'Tips')}</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25, color: 'text.secondary', fontSize: 14, '& li': { mb: 0.75 } }}>
                <li>{t('analyzer.tip1', 'Extract the ZIP and run RunMefirst.bat with sysadmin rights (it creates a SQL Agent job and temporary tables).')}</li>
                <li>{t('analyzer.tip2', 'When collection has finished, use Export in the collector and upload the exported ZIP.')}</li>
                <li>{t('analyzer.tip3', 'Edition and SQL version are detected automatically.')}</li>
                <li>{t('analyzer.tip4', 'Longer collections (a week or more) give more reliable recommendations.')}</li>
              </Box>
              <Alert severity="info" sx={{ mt: 'auto' }}>
                <Typography variant="caption">{t('usageInstructions')}</Typography>
              </Alert>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Results Section */}
      {results && (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {(() => {
              const isBatch = results.batchMode;
              const serverCount = isBatch ? results.results?.filter(r => r.success).length || 0 : 1;
              const avgCpu = isBatch
                ? (results.results?.filter(r => r.success).reduce((s, r) => s + (r.analysis?.cpu?.p95 || 0), 0) / (serverCount || 1)).toFixed(1)
                : results.analysis?.cpu?.p95?.toFixed(1) || '0';
              const avgMem = isBatch
                ? (results.results?.filter(r => r.success).reduce((s, r) => s + (r.analysis?.memory?.p95 || 0), 0) / (serverCount || 1)).toFixed(1)
                : results.analysis?.memory?.p95?.toFixed(1) || '0';
              const totalCost = isBatch
                ? results.results?.filter(r => r.success).reduce((s, r) => s + (r.recommendations?.[0]?.monthlyPrice || 0), 0)
                : results.recommendations?.[0]?.monthlyPrice || 0;
              // BUG-045: servers whose recommended instance has no price (AWS returned nothing) are left out of the total
              const unavailableCount = isBatch
                ? results.results?.filter(r => r.success && r.recommendations?.[0]?.monthlyPrice == null).length || 0
                : (results.recommendations?.[0]?.monthlyPrice == null ? 1 : 0);
              const cpuHot = parseFloat(avgCpu) > 80, memHot = parseFloat(avgMem) > 80;
              const cards = [
                { label: 'Servers', value: serverCount, icon: <Dns />, gradient: GRADIENTS.brand },
                { label: 'Avg CPU P95', value: parseFloat(avgCpu), decimals: 1, suffix: '%', icon: <SpeedIcon />, gradient: cpuHot ? GRADIENTS.amber : GRADIENTS.sky, color: cpuHot ? 'error.main' : undefined },
                { label: 'Avg Memory P95', value: parseFloat(avgMem), decimals: 1, suffix: '%', icon: <Memory />, gradient: memHot ? GRADIENTS.amber : GRADIENTS.violet, color: memHot ? 'error.main' : undefined },
                { label: unavailableCount > 0 ? `Est. Monthly Cost (${unavailableCount} price unavailable)` : 'Est. Monthly Cost',
                  value: unavailableCount >= serverCount ? 'Unavailable' : totalCost, prefix: '$', icon: <AttachMoney />, gradient: GRADIENTS.emerald },
              ];
              return cards.map((c, i) => (
                <Grid item xs={12} sm={6} md={3} key={i} className="anim-fade-up" style={{ animationDelay: `${i * 90}ms` }}>
                  <KpiTile {...c} />
                </Grid>
              ));
            })()}
          </Grid>

          {results.batchMode ? (
            /* Batch Results */
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                {t('analysisResults')}
              </Typography>
              
              <Alert severity="success" sx={{ mb: 3 }}>
                📊 {results.successfulFiles}/{results.totalFiles} files processed successfully
              </Alert>

              {/* Batch CPU/Memory chart */}
              {results.results?.filter(r => r.success).length > 1 && (
                <Box sx={{ mb: 3, maxHeight: 300 }}>
                  <Bar
                    data={{
                      labels: results.results.filter(r => r.success).map(r => r.serverName || r.fileName?.replace('.zip','')),
                      datasets: [
                        { label: 'CPU P95%', data: results.results.filter(r => r.success).map(r => r.analysis?.cpu?.p95 || 0), backgroundColor: 'rgba(54, 162, 235, 0.7)' },
                        { label: 'Memory P95%', data: results.results.filter(r => r.success).map(r => r.analysis?.memory?.p95 || 0), backgroundColor: 'rgba(255, 99, 132, 0.7)' }
                      ]
                    }}
                    options={{ responsive: true, plugins: { legend: { position: 'top' } }, scales: { y: { beginAtZero: true, max: 100 } } }}
                  />
                </Box>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>{t('server')}</strong></TableCell>
                      <TableCell align="center"><strong>Edition</strong></TableCell>
                      <TableCell align="center"><strong>{t('cpuPercent')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('memoryPercent')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('iops')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('throughput')}</strong></TableCell>
                      <TableCell><strong>{t('recommendedInstance')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('monthlyCost')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('details')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.results.map((result, index) => (
                      <React.Fragment key={index}>
                        <TableRow hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {result.success ? (
                                <Chip label="✅" size="small" color="success" sx={{ mr: 1 }} />
                              ) : (
                                <Chip label="❌" size="small" color="error" sx={{ mr: 1 }} />
                              )}
                              <Typography variant="body2" fontWeight="medium">
                                {result.fileName.replace('.zip', '')}
                              </Typography>
                            </Box>
                          </TableCell>
                          {result.success ? (
                            <>
                              <TableCell align="center">
                                <Typography variant="caption">
                                  {result.analysis?.currentServer?.sqlEdition?.replace(' Edition','') || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={`Max: ${result.analysis.cpu.max.toFixed(1)}% | Avg: ${result.analysis.cpu.avg.toFixed(1)}%`}>
                                  <Chip 
                                    label={`${result.analysis.cpu.p95.toFixed(1)}%`}
                                    color={result.analysis.cpu.p95 > 80 ? 'error' : result.analysis.cpu.p95 > 60 ? 'warning' : 'success'}
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={`Max: ${result.analysis.memory.max.toFixed(1)}% | Avg: ${result.analysis.memory.avg.toFixed(1)}%`}>
                                  <Chip 
                                    label={`${result.analysis.memory.p95.toFixed(1)}%`}
                                    color={result.analysis.memory.p95 > 80 ? 'error' : result.analysis.memory.p95 > 60 ? 'warning' : 'success'}
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={`Max: ${result.analysis.iops.max.toLocaleString()}`}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {Math.round(result.analysis.iops.p95).toLocaleString()}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={`Max: ${result.analysis.throughput.max.toFixed(0)} MB/s`}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {result.analysis.throughput.p95.toFixed(0)} MB/s
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Select
                                    value={result.selectedInstance || result.recommendations[0]?.instanceType || ''}
                                    size="small"
                                    sx={{ minWidth: 200 }}
                                    displayEmpty
                                    onChange={(e) => {
                                      const updated = {...results};
                                      updated.results[index].selectedInstance = e.target.value;
                                      setResults({...updated});
                                    }}
                                  >
                                    {result.recommendations?.map((rec, idx) => (
                                      <MenuItem key={idx} value={rec.instanceType}>
                                        <Box>
                                          <Typography variant="body2" fontWeight={rec.isRecommended ? "bold" : "normal"}>
                                            {rec.instanceType} {rec.isRecommended && "⭐"}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {rec.specs?.cpu || "N/A"}vCPU • {rec.specs?.memory || "N/A"}GB • {rec.monthlyPrice == null ? "price unavailable" : `$${rec.monthlyPrice}/mo`}
                                          </Typography>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                {(() => {
                                  const sel = result.selectedInstance || result.recommendations[0]?.instanceType;
                                  const rec = result.recommendations?.find(r => r.instanceType === sel) || result.recommendations[0];
                                  return <Chip label={rec?.monthlyPrice == null ? 'Price unavailable' : `$${rec.monthlyPrice}`} color="primary" icon={<AttachMoney />} />;
                                })()}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="View detailed analysis">
                                  <IconButton
                                    onClick={() => toggleRowExpansion(index)}
                                    size="small"
                                  >
                                    <ExpandMore 
                                      sx={{ 
                                        transform: expandedRows.has(index) ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s'
                                      }} 
                                    />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </>
                          ) : (
                            <TableCell colSpan={8}>
                              <Alert severity="error" size="small">
                                {result.error}
                              </Alert>
                            </TableCell>
                          )}
                        </TableRow>
                        
                        {expandedRows.has(index) && result.success && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ bgcolor: 'grey.50', p: 3 }}>
                              <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="h6" gutterBottom>{t('performanceMetrics')}</Typography>
                                  <Box sx={{ height: 300 }}>
                                    <Line 
                                      data={getPerformanceChartData(result.analysis)}
                                      options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                          title: {
                                            display: true,
                                            text: t('cpuMemoryPerformance')
                                          }
                                        }
                                      }}
                                    />
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="h6" gutterBottom>{t('costComparison')}</Typography>
                                  <Box sx={{ height: 300 }}>
                                    <Bar 
                                      data={getCostComparisonData(result.recommendations)}
                                      options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                          title: {
                                            display: true,
                                            text: t('monthlyCostComparison')
                                          }
                                        }
                                      }}
                                    />
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Current Server Specifications</Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card sx={{ textAlign: 'center', p: 2 }}>
                                        <Speed sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                                        <Typography variant="h6">{t('cpuCores')}</Typography>
                                        <Typography variant="h4" color="info.main">
                                          {result.analysis.currentServer.cores}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          Physical/Logical Cores
                                        </Typography>
                                      </Card>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card sx={{ textAlign: 'center', p: 2 }}>
                                        <Memory sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                                        <Typography variant="h6">{t('totalMemory')}</Typography>
                                        <Typography variant="h4" color="info.main">
                                          {result.analysis.currentServer.memoryGB} GB
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {result.analysis.currentServer.memoryMB} MB
                                        </Typography>
                                      </Card>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card sx={{ textAlign: 'center', p: 2 }}>
                                        <Assessment sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                                        <Typography variant="h6">{t('memoryPerCore')}</Typography>
                                        <Typography variant="h4" color="info.main">
                                          {(() => {
                                            const cores = result.analysis.currentServer.cores;
                                            const memoryGB = result.analysis.currentServer.memoryGB;
                                            
                                            const numericCores = typeof cores === 'string' 
                                              ? parseInt(cores.replace(/[^\\d]/g, '')) 
                                              : cores;
                                            
                                            if (numericCores && memoryGB && numericCores > 0 && memoryGB > 0) {
                                              return Math.round(memoryGB / numericCores);
                                            }
                                            return 'N/A';
                                          })()} GB
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          Memory to CPU Ratio
                                        </Typography>
                                      </Card>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card sx={{ textAlign: 'center', p: 2 }}>
                                        <Settings sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                                        <Typography variant="h6">Lift & Shift Size</Typography>
                                        <Typography variant="h4" color="warning.main">
                                          {result.recommendations?.[0]?.liftAndShiftSize || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          Baseline Instance Size
                                        </Typography>
                                      </Card>
                                    </Grid>
                                  </Grid>

                                  {/* SQL Edition/Version/HT */}
                                  {result.analysis.currentServer.sqlEdition && (
                                  <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="body2">
                                      <strong>Edition:</strong> {result.analysis.currentServer.sqlEdition} | <strong>Version:</strong> {result.analysis.currentServer.sqlVersion || 'N/A'} | <strong>HT:</strong> {result.analysis.currentServer.htStatus || 'N/A'}
                                    </Typography>
                                  </Box>
                                  )}

                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>{t('performanceOverview')}</Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card sx={{ textAlign: 'center', p: 2 }}>
                                        <Speed sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                        <Typography variant="h6">{t('cpuUsage')}</Typography>
                                        <Typography variant="h4" color="primary">{result.analysis.cpu.p95.toFixed(1)}%</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          P95 (sizing) | Avg: {result.analysis.cpu.avg.toFixed(1)}% | Max: {result.analysis.cpu.max.toFixed(1)}%
                                        </Typography>
                                      </Card>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card sx={{ textAlign: 'center', p: 2 }}>
                                        <Memory sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                                        <Typography variant="h6">{t('memoryUsage')}</Typography>
                                        <Typography variant="h4" color="secondary">{result.analysis.memory.p95.toFixed(1)}%</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          P95 (sizing) | Avg: {result.analysis.memory.avg.toFixed(1)}% | Max: {result.analysis.memory.max.toFixed(1)}%
                                        </Typography>
                                      </Card>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card sx={{ textAlign: 'center', p: 2 }}>
                                        <Storage sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                                        <Typography variant="h6">{t('iops')}</Typography>
                                        <Typography variant="h4" color="warning.main">{Math.round(result.analysis.iops.p95).toLocaleString()}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          P95 (sizing) | Avg: {Math.round(result.analysis.iops.avg).toLocaleString()} | Max: {Math.round(result.analysis.iops.max).toLocaleString()}
                                        </Typography>
                                      </Card>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card sx={{ textAlign: 'center', p: 2 }}>
                                        <NetworkCheck sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                                        <Typography variant="h6">{t('throughput')}</Typography>
                                        <Typography variant="h4" color="info.main">{result.analysis.throughput.p95.toFixed(0)} MB/s</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          P95 (sizing) | Avg: {result.analysis.throughput.avg.toFixed(0)} | Max: {result.analysis.throughput.max.toFixed(0)} MB/s
                                        </Typography>
                                      </Card>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="h6" gutterBottom>{t('recommendations')}</Typography>
                                  {result.recommendations.map((rec, recIndex) => (
                                    <Card key={recIndex} variant="outlined" sx={{ mb: 2 }}>
                                      <CardContent>
                                        <Grid container spacing={2} alignItems="center">
                                          <Grid item xs={12} sm={3}>
                                            <Typography variant="h6">{rec.instanceType}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              Score: {rec.score}/100
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} sm={6}>
                                            <Typography variant="body2">
                                              {rec.explanation}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} sm={3}>
                                            <Typography variant="h6" color="primary">
                                              {rec.monthlyPrice == null ? "Price unavailable" : `$${rec.monthlyPrice}/month`}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              {rec.hourlyPrice == null ? "" : `$${rec.hourlyPrice.toFixed(3)}/hour`}
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </Grid>
                              </Grid>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : (
            /* Single File Results */
            <Box>
              {/* Current Server Specifications */}
              {results.analysis.currentServer && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h5" gutterBottom>
                    Current Server Specifications (Lift & Shift Baseline)
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <Speed sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                        <Typography variant="h6">{t('cpuCores')}</Typography>
                        <Typography variant="h4" color="info.main">
                          {results.analysis.currentServer.cores}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Physical/Logical Cores
                        </Typography>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <Memory sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                        <Typography variant="h6">{t('totalMemory')}</Typography>
                        <Typography variant="h4" color="info.main">
                          {results.analysis.currentServer.memoryGB} GB
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {results.analysis.currentServer.memoryMB} MB
                        </Typography>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <Assessment sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                        <Typography variant="h6">{t('memoryPerCore')}</Typography>
                        <Typography variant="h4" color="info.main">
                          {(() => {
                            const cores = results.analysis.currentServer.cores;
                            const memoryGB = results.analysis.currentServer.memoryGB;
                            
                            // Extract numeric value from cores (handle "~5 (estimated)" format)
                            const numericCores = typeof cores === 'string' 
                              ? parseInt(cores.replace(/[^\d]/g, '')) 
                              : cores;
                            
                            if (numericCores && memoryGB && numericCores > 0 && memoryGB > 0) {
                              return Math.round(memoryGB / numericCores);
                            }
                            return 'N/A';
                          })()} GB
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Memory to CPU Ratio
                        </Typography>
                      </Card>
                    </Grid>
                    
                    {/* Lift & Shift Size */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <CloudUpload sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                        <Typography variant="h6">Lift & Shift Size</Typography>
                        <Typography variant="h4" color="warning.main">
                          {results.recommendations && results.recommendations[0] && results.recommendations[0].liftAndShiftSize 
                            ? results.recommendations[0].liftAndShiftSize 
                            : 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Baseline AWS Instance Size
                        </Typography>
                      </Card>
                    </Grid>

                    {/* SQL Edition */}
                    {results.analysis.currentServer.sqlEdition && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <Storage sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                        <Typography variant="h6">SQL Edition</Typography>
                        <Typography variant="h5" color="secondary.main">
                          {results.analysis.currentServer.sqlEdition?.replace(' Edition', '')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Version: {results.analysis.currentServer.sqlVersion || 'N/A'} | HT: {results.analysis.currentServer.htStatus || 'N/A'}
                        </Typography>
                      </Card>
                    </Grid>
                    )}
                  </Grid>
                </Paper>
              )}

              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Performance Analysis
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                      <Speed sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6">{t('cpuUsage')}</Typography>
                      <Typography variant="h4" color="primary">{results.analysis.cpu.max.toFixed(1)}%</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg: {results.analysis.cpu.avg.toFixed(1)}% | P95: {results.analysis.cpu.p95.toFixed(1)}%
                      </Typography>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                      <Memory sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                      <Typography variant="h6">{t('memoryUsage')}</Typography>
                      <Typography variant="h4" color="secondary">{results.analysis.memory.max.toFixed(1)}%</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg: {results.analysis.memory.avg.toFixed(1)}% | P95: {results.analysis.memory.p95.toFixed(1)}%
                      </Typography>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                      <Storage sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                      <Typography variant="h6">{t('iops')}</Typography>
                      <Typography variant="h4" color="warning.main">{results.analysis.iops.max.toLocaleString()}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg: {results.analysis.iops.avg.toLocaleString()} | P95: {results.analysis.iops.p95.toLocaleString()}
                      </Typography>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                      <Assessment sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                      <Typography variant="h6">{t('throughput')}</Typography>
                      <Typography variant="h4" color="success.main">{results.analysis.throughput.max.toFixed(0)} MB/s</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg: {results.analysis.throughput.avg.toFixed(0)} MB/s | P95: {results.analysis.throughput.p95.toFixed(0)} MB/s
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, height: 400 }}>
                  <Line 
                    data={getPerformanceChartData(results.analysis)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: t('performanceMetricsOverview')
                        }
                      }
                    }}
                  />
                </Box>
              </Paper>

              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {t('instanceRecommendations')}
                </Typography>
                
                <Grid container spacing={3}>
                  {results.recommendations.map((rec, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card elevation={3} sx={{ height: '100%', position: 'relative', overflow: 'hidden',
                        ...((rec.isRecommended || index === 0) ? {
                          border: '2px solid transparent',
                          background: (th) => `linear-gradient(${th.palette.background.paper}, ${th.palette.background.paper}) padding-box, ${GRADIENTS.hero} border-box`,
                          boxShadow: '0 10px 30px rgb(var(--c-primary-rgb) / 0.22)',
                          '&::after': { content: '""', position: 'absolute', top: 0, bottom: 0, width: '30%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.30), transparent)', animation: 'shimmerSweep 5s ease-in-out infinite', pointerEvents: 'none' },
                        } : {}) }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Tooltip title={rec.explanation || 'No details available'} arrow placement="top">
                                <Typography variant="h6" component="h3" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                                  {rec.instanceType}
                                </Typography>
                              </Tooltip>
                              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                                {rec.serviceLabel || (rec.serviceType === 'RDS' ? 'RDS (Managed)' : 'EC2 (Self-Managed)')}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {(rec.isRecommended || index === 0) && <Chip size="small" icon={<AutoAwesome sx={{ fontSize: 14 }} />} label="Recommended" sx={{ background: GRADIENTS.brand, color: '#fff', '& .MuiChip-icon': { color: '#fff' } }} />}
                              <Chip
                                label={`Score: ${rec.score ?? 'N/A'}`}
                                color={rec.score != null && rec.score === 100 ? 'success' : 'primary'}
                                size="small"
                              />
                            </Box>
                          </Box>
                          
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">{t('vcpus')}</Typography>
                              <Typography variant="h6">{rec.specs?.cpu || "N/A"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">{t('memory')}</Typography>
                              <Typography variant="h6">{rec.specs?.memory ? `${rec.specs.memory} GB` : "N/A"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">IOPS</Typography>
                              <Typography variant="h6">{rec.specs?.iops?.toLocaleString() || "N/A"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Storage</Typography>
                              <Typography variant="h6">{rec.ebsType?.toUpperCase() || 'N/A'}</Typography>
                            </Grid>
                          </Grid>

                          <Box sx={{ background: (rec.isRecommended || index === 0) ? GRADIENTS.hero : GRADIENTS.sky, backgroundSize: '200% 200%', animation: 'gradientShift 12s ease infinite', color: 'white', p: 2, borderRadius: 2, mb: 2 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                              {rec.monthlyPrice == null ? "Price unavailable" : `$${rec.monthlyPrice}/month`}
                            </Typography>
                            <Typography variant="body2">
                              {rec.hourlyPrice == null ? "" : `$${rec.hourlyPrice.toFixed(3)}/hour`}
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="text.secondary">
                            {rec.explanation}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Side-by-side comparison */}
                {results.recommendations?.[0] && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>Your Server → Recommended RDS</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell><strong>Metric</strong></TableCell>
                          <TableCell align="center"><strong>Current</strong></TableCell>
                          <TableCell align="center"><strong>→</strong></TableCell>
                          <TableCell align="center"><strong>Recommended</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>CPU</TableCell>
                          <TableCell align="center">{results.analysis?.currentServer?.cores || '?'} cores</TableCell>
                          <TableCell align="center">→</TableCell>
                          <TableCell align="center">{results.recommendations[0].specs?.cpu || '?'} vCPUs</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Memory</TableCell>
                          <TableCell align="center">{results.analysis?.currentServer?.memoryGB || '?'} GB</TableCell>
                          <TableCell align="center">→</TableCell>
                          <TableCell align="center">{results.recommendations[0].specs?.memory || '?'} GB</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Instance</TableCell>
                          <TableCell align="center">On-Premises</TableCell>
                          <TableCell align="center">→</TableCell>
                          <TableCell align="center"><strong>{results.recommendations[0].instanceType}</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                )}

              </Paper>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
